/**
 * A mounted micro-app signing in **as itself** (LO-31).
 *
 * This is the half of the kit most likely to be got wrong by a new author and
 * the most damaging when it is, so it is the half that is worth reading before
 * you write a line of your own.
 *
 * ## The mistake this file exists to prevent
 *
 * Permission in this platform is `effective = manifest ∩ user`. Which app a
 * request comes from is decided by the Cognito app client the token was signed
 * for — a claim the caller cannot assert, which is the only reason a manifest
 * means anything.
 *
 * So a remote that borrows the host's token inherits the host's ceiling. The
 * console's manifest is the widest in the system; it declares every type
 * because its job is to show what the kernel holds. A remote running on the
 * console's token would silently read everything the console can, and the Apps
 * screen — the one screen built to make ceilings visible — would show the
 * console's ceiling while the remote quietly enjoyed it. A governance hole in
 * the product whose purpose is preventing governance holes.
 *
 * The host makes that mistake hard: `context` carries no token and no `fetch`
 * wrapper holding one. This file makes the right thing easy, which is the
 * other half of the same job.
 *
 * ## No framework
 *
 * Deliberately zero imports. A remote is loaded by dynamic `import()` and has
 * its own module graph, so anything this file pulled in every remote would
 * ship a copy of. Nothing here needs React, so nothing here has it — a remote
 * that renders with plain DOM pays a few kilobytes for correct auth and not a
 * framework for it.
 */

// ---------------------------------------------------------------------------
// PKCE
//
// The authorization code comes back through a redirect, where anything on the
// machine can see it. The verifier proves we are the client that started the
// flow, so an intercepted code is not enough to get a token.
// ---------------------------------------------------------------------------

const b64url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

async function challengeFor(verifier) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return b64url(digest);
}

async function newVerifier() {
  const value = b64url(crypto.getRandomValues(new Uint8Array(32)));
  return { value, challenge: await challengeFor(value) };
}

const newState = () => b64url(crypto.getRandomValues(new Uint8Array(16)));

/** Read a JWT's payload without verifying it.
 *
 * For display and for expiry, never for a decision. The kernel verifies; a
 * client that trusted its own parse of a token it was handed would be
 * trusting the token's author.
 */
export function claimsOf(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part + "=".repeat((4 - (part.length % 4)) % 4)));
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Storage
//
// Namespaced by client id. Never the host's key, and never a shared one —
// two remotes sharing a storage key is two remotes sharing a ceiling, which
// is the same bug as borrowing the host's token wearing a different hat.
// ---------------------------------------------------------------------------

const tokenKey = (context) => `remote.${context.instance}.token`;

/** A live token for this app from a previous mount, or null. */
export function storedToken(context) {
  const sb = sandboxOf(context);
  if (sb) return live(sb)?.token || null;
  try {
    const token = sessionStorage.getItem(tokenKey(context));
    if (!token) return null;
    const claims = claimsOf(token);
    if (claims.exp && claims.exp * 1000 < Date.now()) {
      sessionStorage.removeItem(tokenKey(context));
      return null;
    }
    return token;
  } catch {
    // Storage can throw outright in a locked-down browser. A remote that
    // cannot cache a token still works; it just authorizes again.
    return null;
  }
}

/** Drop this app's cached token. The *user's* session is the host's to end. */
export function forgetToken(context) {
  const sb = sandboxOf(context);
  if (sb) return forget(sb);
  try {
    sessionStorage.removeItem(tokenKey(context));
  } catch {
    /* nothing stored, nothing to drop */
  }
}

// ---------------------------------------------------------------------------
// Failure, with a stage on it
// ---------------------------------------------------------------------------

/**
 * Why a remote could not get a token, and whether a click would fix it.
 *
 * `needsGesture` is the field worth having. The silent attempt failing is
 * ordinary — it is the *default* in a browser blocking third-party cookies —
 * and it is fixed by a popup, which browsers only allow from a user gesture.
 * A remote that cannot tell "ask them to click" apart from "this is broken"
 * renders the same box for both, and the contract asks for two sentences.
 */
export class RemoteAuthError extends Error {
  constructor(stage, message, cause) {
    super(message);
    this.name = "RemoteAuthError";
    /** `"silent"` | `"popup"` | `"exchange"` | `"sandbox"` */
    this.stage = stage;
    this.cause = cause;
    this.needsGesture = stage === "silent";
  }
}

// ---------------------------------------------------------------------------
// Sandbox (LO-188)
//
// An app running in a sandbox has no Cognito sign-in to attempt. Its page
// config carries `sandbox: { requestId, apiUrl, personas }`, and signing in
// means choosing one of the author's personas and asking the sandbox API to
// mint a token for it. The kernel then decides access exactly as it does for
// any token, from the persona's groups and the request's manifest.
//
// Everything a sandbox shows lives in this file because it must work with no
// framework and no code in the app: one bar, in a shadow root, put on the page
// by the first auth call the app makes. It says what this is, who you are in
// it, and is the picker — a native `<select>`, because the browser already
// supplies its keyboard handling, focus ring, and light, dark and forced
// colours, and every byte here ships in every remote. The cost is measured and
// written down in the README.
//
// What this is not: protection against a hostile app. The app is same-origin
// JavaScript and could always defeat any of it. The guard below stops the
// accidents — a reset stylesheet, an `innerHTML = ""`, a framework replacing
// `<body>` — because those are how a sandbox screenshot comes to read as a real
// install.
// ---------------------------------------------------------------------------

const K = "kernel.sandbox.";

/**
 * The sandbox this page runs in, or `null` on a real install.
 *
 * Absent means absent: no storage is read, no node is created, nothing is
 * listened for. A present-but-incomplete config is still a sandbox — failing
 * *open* here would show a sandbox without its banner, which is the one outcome
 * the whole feature exists to prevent.
 */
export const sandboxOf = (context) =>
  context?.sandbox || globalThis.window?.KERNEL_CONFIG?.sandbox || null;

// `S` is the session minted in this page, for storage that refuses to hold it;
// `U` the bar.
let S, U, pending;

// The token route answers `persona: { name, groups, tags }`; a bare name is
// accepted too. Either way what is kept, shown and announced is the name, a
// string, so a session stored by a build that kept the whole object still
// reads as a name.
const nameOf = (p) => (typeof p === "string" ? p : typeof p?.name === "string" ? p.name : "");

const peek = (sb) => {
  let s;
  try {
    s = JSON.parse(sessionStorage.getItem(K + sb.requestId));
  } catch {
    /* held in memory for this page instead */
  }
  s ||= S;
  return s && { ...s, persona: nameOf(s.persona) };
};

const live = (sb) => {
  const s = peek(sb);
  return s && s.exp - 6e4 > Date.now() ? s : null;
};

const forget = (sb) => {
  S = null;
  try {
    sessionStorage.removeItem(K + sb.requestId);
  } catch {
    /* nothing stored, nothing to drop */
  }
  paint(sb);
};

/** A refusal keeps its status, so the bar can say who said no. */
const refuse = (status, message) => Object.assign(new Error(message), { status });

/**
 * The bearer of the person looking at the page — a requester, a governor or
 * the QA browser — which is what the sandbox API authorizes a persona token
 * against. It arrives as `#viewer_token=…` on the live link, moves to
 * `sessionStorage` at once, and leaves the address bar. It is the *viewer's*
 * token and never a persona's: nothing in the page source or the config can
 * sign a persona in.
 */
function viewer() {
  try {
    const m = /viewer_token=([^&]+)/.exec(location.hash);
    if (m) {
      sessionStorage.setItem(K + "viewer", m[1]);
      history.replaceState(
        null,
        "",
        location.href.replace(/([#&])viewer_token=[^&]*&?/, "$1").replace(/[#&]$/, ""),
      );
    }
    return sessionStorage.getItem(K + "viewer");
  } catch {
    return null;
  }
}

async function mint(sb, name) {
  const v = viewer();
  if (!v) throw refuse(401, "no viewer sign-in reached this page. Open the sandbox from its request.");
  let r;
  try {
    r = await fetch(
      `${sb.apiUrl}/sandbox/${encodeURIComponent(sb.requestId)}/personas/${encodeURIComponent(name)}/token`,
      { method: "POST", headers: { Authorization: `Bearer ${v}` } },
    );
  } catch {
    throw refuse(0, `cannot reach ${sb.apiUrl}`);
  }
  const b = (await r.json().catch(() => null)) || {};
  if (!r.ok || !b.access_token) {
    throw refuse(r.status, r.ok ? "the sandbox answered without a token" : b.detail || b.error || r.statusText || "no reason given");
  }
  S = { token: b.access_token, persona: nameOf(b.persona) || name, exp: Date.now() + (b.expires_in || 3600) * 1e3 };
  try {
    sessionStorage.setItem(K + sb.requestId, JSON.stringify(S));
  } catch {
    /* held in memory for this page instead */
  }
  paint(sb);
  return S;
}

// `all: initial` and `!important` on the host: an app's stylesheet cannot
// reach inside a shadow root, but it can still style the element that holds
// it. `color-scheme` is handed back on purpose, so the controls follow
// whatever scheme the page is in, including the unstamped "system" one.
const HOST =
  "all:initial!important;position:fixed!important;inset:auto 0 0!important;" +
  "z-index:2147483647!important;color-scheme:inherit!important";

const PARTS =
  "<style>div{display:flex;flex-wrap:wrap;gap:4px 12px;align-items:center;padding:6px 12px;" +
  "font:600 14px system-ui,sans-serif;background:#ffd24d;color:#1f1700;border-top:4px double}" +
  "select{font:inherit;max-width:100%}</style>" +
  "<div><span id=s role=status></span><select id=p aria-label=Persona></select><span id=e role=alert></span></div>";

const paint = (sb) => {
  if (!U) return;
  const s = peek(sb)?.persona || "";
  U.s.textContent = `Sandbox: synthetic data, not approved · Persona: ${s || "none chosen"}`;
  U.p.value = s;
};

/** Fired when a persona was chosen with nobody waiting for it. An app that can
 *  re-render calls `preventDefault()` and carries on; one that cannot gets a
 *  reload, because the one thing worse than a sandbox with no banner is
 *  persona B's name over persona A's data. */
const applied = (s) =>
  window.dispatchEvent(new CustomEvent("kernel:persona", { cancelable: true, detail: { persona: s.persona, token: s.token } })) &&
  location.reload();

function install(sb) {
  if (U) return U;
  viewer();
  const host = document.createElement("kernel-sandbox");
  const root = host.attachShadow({ mode: "open" });
  root.innerHTML = PARTS;
  const g = (id) => root.getElementById(id);
  U = { host, s: g("s"), p: g("p"), e: g("e") };
  const ps = sb.personas || [];
  U.p.append(
    new Option(ps.length ? "Choose a persona" : "No personas are defined", ""),
    ...ps.map(
      (x) => new Option(`${x.name} (groups: ${x.groups?.join(", ") || "none"}; tags: ${x.tags?.join(", ") || "none"})`, x.name),
    ),
  );
  U.p.onchange = async () => {
    U.e.textContent = "";
    if (!U.p.value) return;
    try {
      const s = await mint(sb, U.p.value);
      const f = U.next;
      U.next = null;
      (f || applied)(s);
    } catch (x) {
      U.e.textContent = `Refused (${x.status || "no answer"}): ${x.message}`;
      paint(sb);
    }
  };

  // Put it back if the app takes it away. Observation is asynchronous, so a
  // removed bar is back before the next paint.
  const fix = () => {
    const b = document.body;
    if (!b) return;
    if (host.parentNode !== b) b.append(host);
    if (host.getAttribute("style") !== HOST) host.setAttribute("style", HOST);
    watch.observe(b, { childList: true });
  };
  const watch = new MutationObserver(fix);
  watch.observe(document.documentElement, { childList: true });
  watch.observe(host, { attributes: true });
  paint(sb);
  document.body ? fix() : window.addEventListener("DOMContentLoaded", fix);
  return U;
}

/** Wait for the next persona to be chosen, putting the person on the picker. */
function ask(sb, signal) {
  install(sb);
  return (pending ||= new Promise((resolve, reject) => {
    U.next = (s) => {
      pending = null;
      resolve(s);
    };
    signal?.addEventListener("abort", () => {
      pending = U.next = null;
      reject(new Error("cancelled"));
    });
    U.p.focus();
  }));
}

/**
 * A token for this sandbox's current persona: the one already chosen in this
 * tab, else whatever the person picks. Installs the bar.
 */
export async function sandboxToken(sb, signal) {
  install(sb);
  return (live(sb) || (await ask(sb, signal))).token;
}

/** The persona chosen in this tab, or `null`. A string, so it can be a
 *  store's snapshot without allocating. */
export const sandboxPersona = (sb) => peek(sb)?.persona || null;

/**
 * The same token without asking: a live one, else a silent re-mint for the
 * persona last chosen, else `null`. For `resume()`, which has to answer
 * without a screen.
 */
export async function sandboxFresh(sb) {
  install(sb);
  const s = live(sb);
  if (s) return s;
  const was = peek(sb);
  try {
    return was?.persona && viewer() ? await mint(sb, was.persona) : null;
  } catch {
    return null;
  }
}

/** Put the person on the picker as the sign-in screen. */
export async function sandboxSignIn(sb) {
  install(sb).p.focus();
}

/** Forget the persona and ask for another. There is no Cognito session to end. */
export function sandboxSignOut(sb) {
  install(sb);
  forget(sb);
  return sandboxSignIn(sb);
}

/** A persona token issued to this sandbox's client is swapped for the current
 *  persona's, so an app that switched persona without reloading does not send
 *  the old one. A token issued to any other client is sent exactly as given. */
export async function sandboxBearer(sb, token) {
  const s = await sandboxFresh(sb);
  const c = claimsOf(token).client_id;
  return s && c && c === claimsOf(s.token).client_id ? s.token : token;
}

// ---------------------------------------------------------------------------
// The flow
// ---------------------------------------------------------------------------

function authorizeUrl(context, { verifier, state, silent }) {
  const params = new URLSearchParams({
    response_type: "code",
    // This app's client id, which is the whole point: the access token Cognito
    // signs carries it, and the kernel resolves this app's manifest from it.
    client_id: context.instance,
    redirect_uri: context.redirectUri,
    scope: "openid email profile",
    code_challenge_method: "S256",
    code_challenge: verifier.challenge,
    state,
  });
  if (silent) {
    // Ask the authorization endpoint not to show anything. Where it is
    // honoured, no session means an immediate `error=login_required` instead
    // of a login page rendering inside a hidden iframe — the same outcome as
    // our timeout, but in milliseconds and saying why.
    params.set("prompt", "none");
  }
  return `${context.login}/oauth2/authorize?${params}`;
}

/**
 * Wait for the host's callback page to post a code back.
 *
 * `context.redirectUri` is served by the **host**, at the host's origin,
 * because that is where this app is running and therefore the only origin
 * Cognito can be told to redirect to. It postMessages same-origin and nothing
 * else.
 */
function waitForCode(state, timeoutMs, signal) {
  return new Promise((resolve, reject) => {
    const done = (fn, value) => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      fn(value);
    };

    const onMessage = (event) => {
      // Both checks matter. The origin check is what stops any page that can
      // reach this window from feeding it a code; the state check is what
      // stops a stale reply from a previous attempt resolving this one.
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== "kernel-remote-auth") return;
      if (data.state !== state) return;

      if (data.error) {
        done(reject, new Error(`${data.error}: ${data.description || ""}`));
        return;
      }
      if (!data.code) {
        done(reject, new Error("the callback carried no authorization code"));
        return;
      }
      done(resolve, data.code);
    };

    const timer = setTimeout(
      () =>
        done(reject, new Error("timed out waiting for an authorization code")),
      timeoutMs,
    );
    signal?.addEventListener("abort", () => done(reject, new Error("cancelled")));
    window.addEventListener("message", onMessage);
  });
}

async function exchange(context, code, verifier) {
  const response = await fetch(`${context.login}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: context.instance,
      code,
      redirect_uri: context.redirectUri,
      code_verifier: verifier.value,
    }),
  });
  if (!response.ok) {
    throw new RemoteAuthError(
      "exchange",
      `token exchange failed: ${response.status} ${await response.text()}`,
    );
  }
  const body = await response.json();
  // The access token, not the id token. Only the access token carries
  // `client_id`, which is the claim the whole model turns on.
  try {
    sessionStorage.setItem(tokenKey(context), body.access_token);
  } catch {
    // Not fatal. The caller holds the token for this mount either way.
  }
  return body.access_token;
}

/**
 * The silent attempt: a hidden iframe.
 *
 * Invisible when it works. It fails whenever the pool's session cookie is not
 * visible inside a frame — which is the *default* in a browser blocking
 * third-party cookies, not an exotic case. That is why `viaPopup` exists and
 * why the failure deserves a real sentence rather than an empty box.
 */
export async function silently(context, signal) {
  const sb = sandboxOf(context);
  if (sb) return sandboxToken(sb, signal);
  const verifier = await newVerifier();
  const state = newState();

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("title", "authorization");
  frame.style.cssText =
    "position:absolute;width:0;height:0;border:0;left:-9999px";
  frame.src = authorizeUrl(context, { verifier, state, silent: true });
  document.body.appendChild(frame);

  try {
    const code = await waitForCode(state, 8000, signal);
    return await exchange(context, code, verifier);
  } catch (cause) {
    throw cause instanceof RemoteAuthError
      ? cause
      : new RemoteAuthError("silent", cause.message, cause);
  } finally {
    frame.remove();
  }
}

/**
 * The fallback: a popup, which needs a click.
 *
 * A popup is a top-level browsing context, so the session cookie is
 * first-party there and the redirect happens without anything being shown for
 * long enough to read. It cannot be automatic — browsers require a user
 * gesture — which is why the UI asks rather than retrying in the background.
 *
 * Call this from inside a click handler. Called from a timer or a retry loop
 * it is simply blocked, and the blocked case is reported as such rather than
 * as a timeout.
 */
export async function viaPopup(context, signal) {
  const sb = sandboxOf(context);
  if (sb) return sandboxToken(sb, signal);
  const verifier = await newVerifier();
  const state = newState();

  const popup = window.open(
    authorizeUrl(context, { verifier, state, silent: false }),
    "kernel-remote-auth",
    "width=520,height=640",
  );
  if (!popup) {
    throw new RemoteAuthError(
      "popup",
      "the browser blocked the sign-in window; allow popups for this site and try again",
    );
  }
  try {
    const code = await waitForCode(state, 120000, signal);
    return await exchange(context, code, verifier);
  } catch (cause) {
    throw cause instanceof RemoteAuthError
      ? cause
      : new RemoteAuthError("popup", cause.message, cause);
  } finally {
    try {
      popup.close();
    } catch {
      // Already closed, or closed by the callback page. Either is fine.
    }
  }
}

/**
 * The whole no-interaction path: a cached token, else the silent flow.
 *
 * Throws a `RemoteAuthError` with `needsGesture === true` when the silent
 * attempt failed, which is your cue to render the explanation and a button
 * that calls `viaPopup`. It is not your cue to retry, and it is very much not
 * your cue to look for a token somewhere else.
 */
export async function authenticate(context, { signal } = {}) {
  // In a sandbox there is no sign-in to attempt: the picker is the sign-in.
  const sb = sandboxOf(context);
  if (sb) return sandboxToken(sb, signal);
  const cached = storedToken(context);
  if (cached) return cached;
  return silently(context, signal);
}

/**
 * Refuse a host whose contract you do not speak.
 *
 * One line, at the top of `mount`. A remote that skips it and reads a field
 * that moved gets `undefined` and a failure three functions away from the
 * cause.
 */
export function requireContract(context, expected = 1) {
  if (context?.contract !== expected) {
    throw new Error(
      `this app speaks host contract ${expected}, and the host offered ${context?.contract}`,
    );
  }
  return context;
}

/**
 * Read the kernel as this app, with the abort signal wired through.
 *
 * A thin wrapper on purpose. It carries `cache: "no-store"` because a screen
 * about who can see what, served from a cache, is the one kind of stale this
 * product cannot afford; and it returns the parsed body on a refusal rather
 * than throwing it away, because **a refusal is information** — the kernel's
 * `not_in_manifest` names the type and lists what you do declare, and that is
 * the sentence your user needs to see.
 */
export async function readKernel(context, token, path, signal) {
  const response = await fetch(context.api + path, {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
    // Wired through so `unmount` genuinely stops these rather than merely
    // ignoring their answers. A promise nobody is waiting for is still a
    // request the browser is making.
    signal,
  });
  const text = await response.text();
  let body = null;
  try {
    body = JSON.parse(text);
  } catch {
    body = null;
  }
  return {
    ok: response.ok,
    status: response.status,
    body,
    text,
    /** True when the *app's* manifest refused this, not the user's groups.
     *  The two want different sentences on screen. */
    isManifest:
      body?.error === "not_in_manifest" || body?.error === "no_manifest",
  };
}
